import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useSettingsStore } from '../stores/settingsStore';

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
function publicKey(value: string) {
  const raw = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, char => char.charCodeAt(0));
}
async function workerConsent(registration: ServiceWorkerRegistration, enabled?: boolean) {
  if (!registration.active) throw new Error('A PWA ainda não está pronta. Reabra o app.');
  return new Promise<boolean>((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => { channel.port1.close(); reject(new Error('Não foi possível confirmar a configuração da PWA.')); }, 5000);
    channel.port1.onmessage = event => {
      window.clearTimeout(timer); channel.port1.close();
      if (event.data?.ok && typeof event.data.enabled === 'boolean') resolve(event.data.enabled);
      else reject(new Error('Não foi possível confirmar o consentimento. Reabra a PWA atualizada.'));
    };
    registration.active!.postMessage(enabled === undefined ? { type: 'PUSH_STATUS' } : { type: 'PUSH_CONSENT', enabled }, [channel.port2]);
  });
}
export function ReminderSettings() {
  const { token } = useAuthStore();
  const { timeZone, saveTimeZone } = useSettingsStore();
  const [zone, setZone] = useState(timeZone);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => { setZone(timeZone); }, [timeZone]);
  useEffect(() => {
    if (!token || !supported()) return;
    navigator.serviceWorker.getRegistration().then(async registration => {
      const subscription = await registration?.pushManager.getSubscription();
      if (!subscription) return;
      const status = await api.post<{ disabled: number; lastError: string | null }>('/api/push/status', { endpoint: subscription.endpoint });
      const consent = await workerConsent(registration!);
      setEnabled(Notification.permission === 'granted' && !status.disabled && consent);
      if (status.lastError) setMessage('A assinatura expirou ou falhou. Ative novamente para reconectar.');
      else if (!consent && !status.disabled) setMessage('Notificações desativadas na PWA. Ative novamente para receber avisos neste dispositivo.');
    }).catch(() => { setEnabled(false); setMessage('Não foi possível verificar as notificações deste dispositivo. Reabra a PWA atualizada.'); });
  }, [token]);
  const activate = async () => {
    setBusy(true); setMessage('');
    try {
      // Permission is requested only by this explicit device-specific click.
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new Error('Permissão não concedida. Autorize notificações nas configurações do navegador.');
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration?.active) throw new Error('A PWA ainda não está pronta. Reabra a versão publicada do app.');
      const { publicKey: key } = await api.get<{ publicKey: string }>('/api/push/key');
      let subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const status = await api.post<{ disabled: number }>('/api/push/status', { endpoint: subscription.endpoint });
        const expected = publicKey(key);
        const current = subscription.options.applicationServerKey ? new Uint8Array(subscription.options.applicationServerKey) : null;
        if (status.disabled || (current && (current.length !== expected.length || current.some((byte, index) => byte !== expected[index])))) {
          await subscription.unsubscribe(); subscription = null;
        }
      }
      subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: publicKey(key) });
      const json = subscription.toJSON();
      await api.post('/api/push/subscriptions', { consent: true, subscription: { endpoint: subscription.endpoint, keys: json.keys } });
      await workerConsent(registration, true);
      setEnabled(true); setMessage('Notificações ativadas neste dispositivo.');
    } catch (error) { setMessage((error as Error).message); }
    finally { setBusy(false); }
  };
  const deactivate = async () => {
    setBusy(true); setMessage('');
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) await workerConsent(registration, false);
      setEnabled(false);
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await api.post('/api/push/unsubscribe', { endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }
      setEnabled(false); setMessage('Notificações desativadas neste dispositivo.');
    } catch (error) { setMessage((error as Error).message); }
    finally { setBusy(false); }
  };
  return <section className="card" style={{ marginBottom: '1.5rem', flexShrink: 0 }}>
    <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>⏰ Lembretes e notificações</h2>
    <label htmlFor="reminder-timezone">Fuso horário dos novos lembretes</label>
    <input id="reminder-timezone" value={zone} onChange={e => setZone(e.target.value)} placeholder="America/Sao_Paulo" style={{ margin: '0.5rem 0' }} />
    <button className="btn btn-secondary" disabled={!token || busy || !zone.trim()} onClick={async () => {
      setBusy(true);
      try { await saveTimeZone(zone.trim()); setMessage('Fuso salvo. Séries existentes mantêm seu fuso original.'); }
      catch (error) { setMessage((error as Error).message); }
      finally { setBusy(false); }
    }}>Salvar fuso horário</button>
    <p style={{ fontSize: '0.8125rem', color: 'var(--fg-muted)', margin: '0.75rem 0' }}>
      Ative neste dispositivo para receber avisos com o app fechado. No iPhone/iPad, instale a PWA na tela inicial primeiro.
      Os avisos mostram o título e a descrição. O navegador e o sistema podem atrasar ou bloquear avisos; os lembretes continuam disponíveis no app.
    </p>
    {supported() ? <button className="btn btn-primary" disabled={!token || busy} onClick={enabled ? deactivate : activate}>
      {busy ? 'Aguarde…' : enabled ? 'Desativar notificações neste dispositivo' : 'Ativar notificações neste dispositivo'}
    </button> : <p>Este navegador não oferece Web Push. Abra em um navegador compatível.</p>}
    {message && <p role="status" style={{ marginTop: '0.75rem', overflowWrap: 'anywhere' }}>{message}</p>}
  </section>;
}
